import { BasePresenter } from "core/presenter";

import {
  ProductRepository,
  DevolutionSellableReportRepository
} from "core/repository";

import Moment from "moment";
import * as FileSaver from "file-saver";

import { fn } from "util";

export default class DevolutionSellableReportPresenter extends BasePresenter {
  initialState() {
    const urlParams = new URLSearchParams(window.location.hash);
    const startDate = Moment(urlParams.get("inicio"), "DD-MM-YYYY");
    const endDate = Moment(urlParams.get("fim"), "DD-MM-YYYY");
    return {
      loading: true,
      filters: {
        dateFrom: startDate.isValid()
          ? startDate
          : Moment()
              .startOf("day")
              .subtract(7, "days"),
        dateTo: endDate.isValid() ? endDate : Moment().endOf("day"),
        devolutionCode: ""
      },
      pagination: {
        page: 1,
        perPage: 25,
        total: 0
      },
      summary: {
        total: 0
      },
      results: [],
      order: {
        field: "created_at",
        direction: "DESC"
      },
      productSearchOptions: {
        online: true,
        repository: ProductRepository,
        order: [{ field: "name", direction: "ASC" }],
        pagination: { page: 1, perPage: 25 },
        filter: filter => this.getProductFilters(filter)
      },
      product: {
        name: "",
        uuid: null
      },
      user: {
        name: "",
        uuid: null
      },
      mode: "default"
    };
  }

  async onStart() {
    this.load();
  }

  async load() {
    this.viewModel.loading = true;
    this.viewModel.results = [];

    let resultsPromise;

    if (this.viewModel.product.uuid) {
      this.viewModel.mode = "default";
      resultsPromise = this.loadDevolutionResults("default");
    } else {
      this.viewModel.mode = "product";
      resultsPromise = this.loadDevolutionResults("product");
    }

    await Promise.all([resultsPromise, this.loadSummary()]);

    this.viewModel.loading = false;
  }

  async loadDevolutionResults(type) {
    this.renewCancelToken();

    const filters = await this.getReportFilters();
    let repositoryResult;
    if (type === "product") {
      repositoryResult = await DevolutionSellableReportRepository.getDevolutionSellabletGroupReport(
        {
          ...filters,
          order: this.getOrder(),
          page: this.viewModel.pagination.page
        }
      );
    } else {
      repositoryResult = await DevolutionSellableReportRepository.getDevolutionSellabletReport(
        {
          ...filters,
          order: this.getOrder(),
          page: this.viewModel.pagination.page
        }
      );
    }

    if (repositoryResult.success) {
      this.viewModel.results = repositoryResult.data.devolutions;
      this.viewModel.pagination = {
        ...this.viewModel.pagination,
        total: repositoryResult.data.pagination.total_items,
        perPage: repositoryResult.data.pagination.per_page
      };
    } else if (!repositoryResult.userCancelled) {
      this.sendNotification({
        type: "error",
        title: "Oops",
        content:
          "Não foi possível carregar os produtos.\nTente novamente mais tarde ou entre em contato com o suporte."
      });
    }
  }

  async loadSummary() {
    const filters = await this.getReportFilters();
    let summaryResult = await DevolutionSellableReportRepository.getDevolutionSellableTotal(
      filters
    );
    if (summaryResult.success) {
      this.viewModel.summary.total = summaryResult.data.total;
    }
  }

  async getReportFilters() {
    const { product, filters } = this.viewModel;
    const { dateFrom, dateTo } = filters;
    const currentUser = await this.mRuntime.get("currentUser");
    const { company_uuid } = currentUser;
    return {
      from: Moment(dateFrom)
        .startOf("day")
        .toISOString(),
      to: Moment(dateTo)
        .endOf("day")
        .toISOString(),
      company_uuid,
      status: "active",
      sellable_uuid: product ? product.uuid : null,
      devolutionCode: filters.devolutionCode
    };
  }

  getProductFilters(query) {
    return [
      { connector: "OR", field: "code", operation: "LIKE", value: query },
      { connector: "OR", field: "name", operation: "LIKE", value: query },
      { connector: "AND", field: "deleted", operation: "NOT", value: "true" }
    ];
  }

  async getUserFilters(query) {
    const companyUuid = (await this.mRuntime.get("currentUser")).company_uuid;
    const filters = [];

    if (query) {
      ["name", "username"].forEach(field =>
        filters.push({
          connector: "OR",
          field,
          operation: "LIKE",
          value: query
        })
      );
    }

    filters.push(
      { connector: "AND", field: "deleted", operation: "NOT", value: true },
      {
        connector: "AND",
        field: "company_uuid",
        operation: "EQ",
        value: companyUuid
      }
    );

    return filters;
  }

  async onDownload(format) {
    this.viewModel.loading = true;

    switch (format) {
      case "csv":
        await this._downloadCSV();
        break;

      default:
        this.sendNotification({
          type: "error",
          title: "Oops...",
          content: "Ainda não suportamos esse formato"
        });
    }

    this.viewModel.loading = false;
  }

  async _downloadCSV() {
    const filters = await this.getReportFilters();
    let content = [];

    if (this.viewModel.mode === "product") {
      let results = await DevolutionSellableReportRepository.getDevolutionSellabletGroupReport(
        filters
      );
      content = ["Codigo; Nome; Quantidade Devolvida; Total Devolvido"];
      results.data.devolutions.forEach(item => {
        const totalValue = fn.centsToReal(item.sellable_total_value || 0);
        content.push(
          `${item.sellable_code || "-"};  ${item.sellable_name ||
            "-"};${item.sellable_quantity || "-"};${totalValue}`
        );
      });
    } else {
      let results = await DevolutionSellableReportRepository.getDevolutionSellableReportCSV(
        filters
      );
      content = [
        "Data; Valor Total; Codigo do Produto; Nome Produto; Quantidade; Devolucao; Vendedor"
      ];
      results.data.devolutions.forEach(item => {
        const totalValue = fn.centsToReal(item.total_value);
        const salesmanName = item.salesman.name
          ? item.salesman.name
          : "Vendedor nao cadastrado";
        const creditCouponCode = item.credit_coupon
          ? item.credit_coupon.code
          : "-";
        const date = Moment(item.created_at).format("DD/MM/YYYY HH:mm");
        content.push(
          `${date};  ${totalValue};${item.sellable.code};${item.sellable.name};${item.quantity};${creditCouponCode};${salesmanName}`
        );
      });
    }

    const contentBlob = new Blob([content.join("\n")], {
      type: "application/csv;charset=utf-8"
    });
    FileSaver.saveAs(contentBlob, "relatorio-de-devolucao-por-produto.csv");
  }

  getOrder() {
    const { order } = this.viewModel;
    return order.direction === "DESC" ? `-${order.field}` : order.field;
  }

  onSelectProduct(selectedProduct) {
    this.viewModel.product = selectedProduct;
    this.viewModel.pagination.page = 1;

    if (selectedProduct.uuid && this.viewModel.mode === "product") {
      this.viewModel.order = { field: "sellable_code", direction: "ASC" };
    } else {
      this.viewModel.order = { field: "created_at", direction: "DESC" };
    }
    this.load();
  }

  onSelectUser(selectedUser) {
    this.viewModel.user = selectedUser;
    this.viewModel.pagination.page = 1;
    this.load();
  }

  onFilterChange(filters) {
    if (filters.dateFrom && filters.dateTo) {
      window.location.hash = `?&inicio=${Moment(filters.dateFrom).format(
        "DD-MM-YYYY"
      )}&fim=${Moment(filters.dateTo).format("DD-MM-YYYY")}`;
    }
    for (const key in filters) {
      if (filters[key] !== this.viewModel.filters[key]) {
        this.viewModel.filters = {
          ...this.viewModel.filters,
          ...filters
        };
        this.viewModel.pagination.page = 1;

        this.load();
        break;
      }
    }
  }

  onPageChange(page) {
    this.viewModel.pagination.page = page;
    this.load();
  }

  onOrderChange(field, direction) {
    this.viewModel.order = { field, direction };
    this.load();
  }
}



// WEBPACK FOOTER //
// ./src/components/app/management/devolution-sellable-report/presenter.js